/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useRef, useState, useEffect, CSSProperties } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from 'styled-components';
import { joinDataTestId, resolveComponentLocator } from '@motili/ui';

type Props = {
    text: string;
    className?: string;
    dataTestId?: string;
    style?: CSSProperties;
    tooltipStyle?: CSSProperties;
    tooltipPlacement?:
        | 'bottom-end'
        | 'bottom-start'
        | 'bottom'
        | 'left-end'
        | 'left-start'
        | 'left'
        | 'right-end'
        | 'right-start'
        | 'right'
        | 'top-end'
        | 'top-start'
        | 'top';
};

const Content = styled.span`
    text-align: left;
    width: 100%;
`;
export const EllipsisTooltip = ({
    text,
    className,
    style,
    tooltipStyle,
    tooltipPlacement,
    dataTestId = 'ellipsis-tooltip',
}: Props) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = textRef.current;
        if (el) {
            setShowTooltip(el.scrollWidth > el.clientWidth);
        }
    }, [text, style]);

    const content = (
        <div
            ref={textRef}
            className={className}
            style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...style,
            }}
            data-test-id={joinDataTestId([dataTestId, resolveComponentLocator('content', 'label', dataTestId)])}
        >
            {text}
        </div>
    );

    return showTooltip ? (
        <Tooltip
            title={text}
            placement={tooltipPlacement || 'top'}
            arrow={false}
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: '#ffffff',
                        color: '#333333',
                        fontFamily: 'Nunito Sans',
                        fontWeight: 400,
                        fontSize: '16px',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0px 2px 6px 0px #00000029',
                        borderRadius: '4px',
                        padding: '12px',
                        display: 'inline-block',
                        maxWidth: '90vw',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        ...tooltipStyle,
                    },
                },
            }}
            data-test-id={`${dataTestId}-tooltip`}
        >
            <Content>{content}</Content>
        </Tooltip>
    ) : (
        <Content>{content}</Content>
    );
};
